/**
 * session.js — lets a tutorial survive a page reload.
 *
 * A tutorial spans four routes and a dozen server round trips, so a refresh in
 * the middle of it must not drop the user back to a half-built scenario with no
 * guidance. The step index and the ids learned so far are enough to resume.
 *
 * sessionStorage, not localStorage: the session belongs to this tab. A second
 * tab opened on the same editor gets the ordinary, unguided experience rather
 * than a second overlay fighting the first.
 */

const STORAGE_KEY = 'tutorial:session';

/**
 * @returns {{tutorialId: string, stepIndex: number, entities: object, version: number}|null}
 */
export function readSession() {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.tutorialId !== 'string') return null;
        return {
            tutorialId: parsed.tutorialId,
            stepIndex: Number.isInteger(parsed.stepIndex) ? parsed.stepIndex : 0,
            entities: parsed.entities && typeof parsed.entities === 'object' ? parsed.entities : {},
            version: parsed.version,
        };
    } catch (error) {
        return null;
    }
}

export function writeSession({ tutorialId, stepIndex, entities, version }) {
    try {
        window.sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ tutorialId, stepIndex, entities, version })
        );
    } catch (error) {
        // A full or disabled store is not a reason to stop the tutorial; it just
        // means a refresh will not resume it.
    }
}

export function clearSession() {
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        /* nothing to clear */
    }
}

export const SESSION_STORAGE_KEY = STORAGE_KEY;
